import { css, StyleSheet } from 'aphrodite';
import { type FC, useMemo, useState } from 'react';

import { Button } from '../../../web/components-v2/Button';
import type { CoachMessage } from '../ai';
import type { CallState } from '../call';
import type { KeywordPin } from '../pins';
import { CallStrip } from './CallStrip';
import { PinGrid, type PinTile } from './PinGrid';

/**
 * Props for {@link CenterColumn}. All data is injected from the top-level
 * `FocusedLeadView` container; the column itself holds no server state.
 */
export type CenterColumnProps = {
	callState: CallState;
	muted: boolean;
	onAnswer: () => void;
	onReject: () => void;
	onHangup: () => void;
	onToggleMute: () => void;
	/**
	 * End the active AI voice-roleplay ("Spar") practice session. Forwarded to
	 * CallStrip's `practice`-state End button. No-op when the strip isn't in the
	 * `practice` status.
	 */
	onEndPractice?: () => void;
	onEnableAudio: () => void;
	/**
	 * Send a DTMF digit on the connected call. Forwarded to CallStrip's
	 * inline dialpad popover. Optional so consumers (e.g. tests) can omit
	 * it; the dialpad toggle is hidden when this is not provided.
	 */
	onSendDigit?: (digit: string) => void;
	/**
	 * True while the Twilio SDK is trying to recover from a transient audio
	 * drop on an active call. Forwarded to CallStrip so the connected strip
	 * surfaces an amber "Reconnecting…" indicator until the next
	 * `call:reconnected` (or `call:ended`).
	 */
	reconnecting?: boolean;

	/**
	 * Preferred display for the active caller/callee on the dark CallStrip,
	 * e.g. `"Acme Corp · Patrick Walters"`. Forwarded as `callerDisplay` so
	 * the strip shows the lead identity instead of the raw E.164 number while
	 * dialing/connected/ringing. Falls back to the call's E.164 string when
	 * absent (e.g. before lead data has loaded).
	 */
	peerDisplay?: string;

	/**
	 * The rep's outbound caller-ID (E.164). Forwarded to CallStrip as
	 * `fromNumber` to render the "Calling from …" sub-line on the dialing /
	 * outbound-connected strips.
	 */
	fromNumber?: string;

	/**
	 * Display for the currently RINGING inbound caller, resolved from the
	 * server-side phone-call record + lead contacts. Takes precedence over
	 * `peerDisplay` while the strip is in `ringing` status — the rep needs
	 * to know who is calling them, which may not be the currently-loaded
	 * lead.
	 */
	inboundDisplay?: string;
	/**
	 * Optional click handler that opens the ringing caller's lead in the
	 * focused view without answering. Makes the caller name in the ringing
	 * strip a link so the rep can preview the lead before deciding.
	 */
	onOpenInboundLead?: () => void;

	/** Full chronological message stream. Oldest first — matches `useCoaching`. */
	messages: CoachMessage[];
	/** IDs the user has pinned to the rail. */
	pinnedIds: string[];
	onTogglePin: (id: string) => void;

	/** True while the AI is streaming a reply — drives the hero's streaming dot. */
	isResponding: boolean;

	/** Sticky-bottom conversation starters (parent filters to enabled `trigger='starter'`). */
	starters: KeywordPin[];
	onSendStarter: (pin: KeywordPin) => void;

	/**
	 * Realtime-coaching websocket connection state. When defined, a small
	 * green/red dot is rendered next to the hero eyebrow with a hover tooltip
	 * on the live tile. Omit when coaching is not active.
	 */
	wsConnected?: boolean;

	/**
	 * If true and the call is connected, the "Get suggestion" button is shown.
	 * Mirrors the legacy gating: visible only when the user has opted out of
	 * automatic suggestions (`coffeePreferences.autoSuggestionsEnabled === false`).
	 */
	showGetSuggestionButton?: boolean;
	onRequestSuggestion?: () => void;

	/**
	 * If true, the "Get summary" button is shown above the pinned-card stack.
	 * Set by the parent on `call:ended` and cleared once the user clicks the
	 * button or a new conversation begins.
	 */
	showGetSummaryButton?: boolean;
	onRequestSummary?: () => void;
};

/**
 * The middle column: `CallStrip`, an optional action row, the pinned-tile grid, and a one-line row of
 * conversation starters.
 *
 * The grid replaced a vertical stack in which a hero card capped at 40vh and a fixed-height Game Plan
 * left every other pin sharing one short scroll well at the bottom — which reps hit on every call,
 * since three pins arrive automatically before anyone touches anything. The latest AI message is now
 * a tile in that grid flagged LIVE rather than a separate hero, and the starters collapsed from a row
 * each to a single chip line; both changes exist to give the tiles their height back.
 *
 * `PinGrid` owns the layout shape. This component owns which messages become tiles and in what order.
 */
export const CenterColumn: FC<CenterColumnProps> = ({
	callState,
	muted,
	onAnswer,
	onReject,
	onHangup,
	onToggleMute,
	onEndPractice,
	onEnableAudio,
	onSendDigit,
	reconnecting,
	peerDisplay,
	fromNumber,
	inboundDisplay,
	onOpenInboundLead,
	messages,
	pinnedIds,
	onTogglePin,
	isResponding,
	starters,
	onSendStarter,
	wsConnected,
	showGetSuggestionButton,
	onRequestSuggestion,
	showGetSummaryButton,
	onRequestSummary,
}) => {
	// Which demoted pins the rep pulled back into the grid, newest first. Kept local to the column: it
	// is a view preference for this conversation, not something worth persisting.
	const [promotedIds, setPromotedIds] = useState<string[]>([]);

	const tiles = useMemo<PinTile[]>(() => {
		const pinned = messages.filter(m => pinnedIds.includes(m.id));
		const gamePlan = pinned.find(m => m.pinnedKeyword === 'GAME PLAN');

		// Promoted pins jump ahead of the rest so a chip click actually brings the tile on screen.
		const others = pinned
			.filter(m => m !== gamePlan)
			.sort((a, b) => promotedIds.indexOf(b.id) - promotedIds.indexOf(a.id));

		// findLast() is ES2023 and not supported in Firefox 91 ESR (in our browserslist), so walk back
		// manually. Pinned assistant messages are skipped: they already hold a tile of their own, and the
		// auto-pin contract makes that trivially reachable (the first reply becomes the Game Plan).
		let live: CoachMessage | undefined;
		for (let i = messages.length - 1; i >= 0; i--) {
			const m = messages[i];
			if (m.role !== 'assistant') continue;
			if (pinnedIds.includes(m.id)) continue;
			live = m;
			break;
		}

		return [
			...(gamePlan ? [{ msg: gamePlan, kind: 'lead' as const }] : []),
			...others.map(msg => ({ msg, kind: 'pin' as const })),
			// Live sorts last so mid-call suggestions always appear in the same place.
			...(live ? [{ msg: live, kind: 'live' as const }] : []),
		];
	}, [messages, pinnedIds, promotedIds]);

	const showSuggestionButton = Boolean(
		showGetSuggestionButton && onRequestSuggestion && callState.status === 'connected'
	);
	const showSummaryButton = Boolean(showGetSummaryButton && onRequestSummary);
	const showActionRow = showSuggestionButton || showSummaryButton;

	return (
		<div className={css(styles.col)}>
			<CallStrip
				callState={callState}
				muted={muted}
				onAnswer={onAnswer}
				onReject={onReject}
				onHangup={onHangup}
				onToggleMute={onToggleMute}
				onEndPractice={onEndPractice}
				onEnableAudio={onEnableAudio}
				callerDisplay={peerDisplay}
				fromNumber={fromNumber}
				inboundDisplay={inboundDisplay}
				onOpenInboundLead={onOpenInboundLead}
				onSendDigit={onSendDigit}
				reconnecting={reconnecting}
			/>

			{showActionRow && (
				<div className={css(styles.actionRow)}>
					{showSuggestionButton && (
						<Button
							variant='primary'
							size='sm'
							onClick={onRequestSuggestion}
							disabled={isResponding}
							data-testid='get-suggestion-button'
						>
							{isResponding ? 'Analyzing…' : 'Get suggestion'}
						</Button>
					)}
					{showSummaryButton && (
						<Button
							variant='outline'
							size='sm'
							onClick={onRequestSummary}
							disabled={isResponding}
							data-testid='get-summary-button'
						>
							{isResponding ? 'Generating summary…' : 'Get summary'}
						</Button>
					)}
				</div>
			)}

			<PinGrid
				tiles={tiles}
				onUnpin={onTogglePin}
				onPromote={id => setPromotedIds(prev => [...prev.filter(existing => existing !== id), id])}
				isResponding={isResponding}
				wsConnected={wsConnected}
			/>

			{starters.length > 0 && (
				<div className={css(styles.starterFooter)}>
					<span className={css(styles.starterLabel)}>ADD</span>
					{starters.map(p => (
						<button
							key={p.id}
							type='button'
							className={css(styles.starterChip)}
							onClick={() => onSendStarter(p)}
							title={`Send: ${p.label}`}
						>
							<span className={css(styles.starterDot)} style={{ background: p.color }} />
							{p.label}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

const styles = StyleSheet.create({
	col: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
		minHeight: 0,
		flex: 1,
		background: 'var(--bg)',
	},
	actionRow: {
		display: 'flex',
		gap: 8,
		padding: '10px 20px 0',
		flexShrink: 0,
	},
	// One line of chips rather than a row per starter — the old footer cost about 90px of tile height.
	starterFooter: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 8,
		padding: '9px 20px 12px',
		borderTop: '1px solid var(--border)',
		background: 'var(--bg)',
	},
	starterLabel: {
		fontFamily: 'var(--font-mono)',
		fontSize: 9.5,
		fontWeight: 600,
		letterSpacing: '0.1em',
		color: 'var(--ink-faint)',
	},
	starterChip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		fontSize: 11,
		padding: '4px 10px',
		borderRadius: 999,
		border: '1px solid var(--border)',
		background: 'var(--bg-elev)',
		color: 'var(--ink)',
		cursor: 'pointer',
		whiteSpace: 'nowrap',
		':hover': {
			background: 'var(--hover)',
		},
	},
	starterDot: {
		width: 6,
		height: 6,
		borderRadius: '50%',
		flexShrink: 0,
	},
});
